import React, { useEffect, useMemo, useState } from "react";
import workspaceData from "../data/private/workspace.json";
import flowGuidanceData from "../data/private/flowGuidance.json";
import VastuCompassPlaceholder from "../components/VastuCompassPlaceholder";
import { completeProgressStep, logActivity } from "../utils/activity";
import normalizeApiError from "../utils/normalizeApiError";
import FlowGuidanceBanner from "../components/FlowGuidanceBanner";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import KeyValueTable from "../components/ui/KeyValueTable";
import { getWorkspaceLayouts, getWorkspaceTemplates } from "../api/workspace.api";

export default function DashboardWorkspace() {
  const [primaryLabel, secondaryLabel] = workspaceData.placeholder
    .split("|")
    .map((item) => item.trim());
  const [templates, setTemplates] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(null);
  const [activeLayoutIndex, setActiveLayoutIndex] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingLayouts, setLoadingLayouts] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);
  const [layoutsError, setLayoutsError] = useState(null);
  const [mappedZones, setMappedZones] = useState([]);
  const hasWorkspaceData =
    workspaceData.placeholder.trim() || workspaceData.notes.trim();
  const [dismissed, setDismissed] = useState(
    Boolean(localStorage.getItem("myv_guidance_workspace"))
  );
  const selectedProperty = localStorage.getItem("myv_selected_property");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await getWorkspaceTemplates();
        const nextTemplates = Array.isArray(data) ? data : [];
        setTemplates(nextTemplates);
        setActiveTemplateIndex(nextTemplates.length ? 0 : null);
        setTemplatesError(null);
      } catch (error) {
        setTemplates([]);
        setActiveTemplateIndex(null);
        setTemplatesError(normalizeApiError(error));
        logActivity("api_error", { module: "workspace" });
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    const fetchLayouts = async () => {
      try {
        const data = await getWorkspaceLayouts();
        const nextLayouts = Array.isArray(data) ? data : [];
        setLayouts(nextLayouts);
        setActiveLayoutIndex(nextLayouts.length ? 0 : null);
        setLayoutsError(null);
      } catch (error) {
        setLayouts([]);
        setActiveLayoutIndex(null);
        setLayoutsError(normalizeApiError(error));
        logActivity("api_error", { module: "workspace" });
      } finally {
        setLoadingLayouts(false);
      }
    };
    fetchLayouts();
  }, []);

  const zones = useMemo(() => workspaceData.dragDrop?.zones || [], []);

  const handleZoneDrop = (zone) => {
    setMappedZones((prev) => (prev.includes(zone) ? prev : [...prev, zone]));
    logActivity("workspace_action_triggered", { zone });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-slate-900">
        {workspaceData.title}
      </h1>
      {!selectedProperty && (
        <FlowGuidanceBanner
          eventKey="workspace-missing-property"
          title={flowGuidanceData.workspace.missingProperty.title}
          description={flowGuidanceData.workspace.missingProperty.description}
          meta={{ page: "workspace", missing: "property" }}
        />
      )}
      <div className="card card-hover p-6" title={workspaceData.notes}>
        <VastuCompassPlaceholder />
        {hasWorkspaceData ? (
          <>
            <p className="mt-4 text-sm text-slate-600">
              {workspaceData.placeholder}
            </p>
            <p className="mt-4 text-sm text-slate-600">{workspaceData.notes}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                className="btn btn-primary"
            onClick={() => logActivity("workspace_cta", { label: primaryLabel })}
              >
                {primaryLabel}
              </button>
              <button
                className="btn btn-secondary"
            onClick={() =>
              logActivity("workspace_cta", { label: secondaryLabel })
            }
              >
                {secondaryLabel}
              </button>
            </div>
            <div className="mt-6">
              <p className="text-sm font-semibold text-slate-900">
                {workspaceData.templatesTitle}
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {loadingTemplates
                  ? Array.from({ length: 4 }).map((_, index) => (
                      <div
                        key={`template-skeleton-${index}`}
                        className="card p-4"
                      >
                        <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
                        <div className="mt-3 h-2 w-full animate-pulse rounded bg-slate-100" />
                      </div>
                    ))
                  : templates.map((template, index) => (
                      <button
                        key={`template-${index}`}
                        className={`card p-4 text-left transition ${
                          activeTemplateIndex === index
                            ? "border-amber-500 text-slate-900 shadow-md"
                            : "text-slate-600 hover:border-amber-300"
                        }`}
                        onClick={() => {
                          setActiveTemplateIndex(index);
                          logActivity("template_loaded", { index });
                          logActivity("workspace_template_loaded", { index });
                          completeProgressStep("layout_selected");
                        }}
                      >
                        <p className="text-sm font-semibold text-slate-900">
                          Template {index + 1}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {typeof template === "string" ? template : "Template data"}
                        </p>
                      </button>
                    ))}
              </div>
              {Number.isInteger(activeTemplateIndex) && templates[activeTemplateIndex] && (
                <div className="mt-4">
                  {typeof templates[activeTemplateIndex] === "string" ? (
                    <div className="card p-4 text-sm text-slate-600">
                      {templates[activeTemplateIndex]}
                    </div>
                  ) : (
                    <KeyValueTable
                      data={templates[activeTemplateIndex]}
                      title="Template details"
                    />
                  )}
                </div>
              )}
              {templatesError && (
                <div className="mt-4">
                  <ErrorState error={templatesError} />
                </div>
              )}
            </div>
            <div className="mt-6">
              <p className="text-sm font-semibold text-slate-900">
                {workspaceData.layoutsTitle}
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {loadingLayouts
                  ? Array.from({ length: 2 }).map((_, index) => (
                      <div
                        key={`layout-skeleton-${index}`}
                        className="card p-4"
                      >
                        <div className="h-3 w-28 animate-pulse rounded bg-slate-200" />
                        <div className="mt-3 h-2 w-full animate-pulse rounded bg-slate-100" />
                      </div>
                    ))
                  : layouts.length === 0
                  ? null
                  : layouts.map((layout, index) => (
                      <button
                        key={`layout-${index}`}
                        className={`card p-4 text-left transition ${
                          activeLayoutIndex === index
                            ? "border-amber-500 text-slate-900 shadow-md"
                            : "text-slate-600 hover:border-amber-300"
                        }`}
                        onClick={() => {
                          setActiveLayoutIndex(index);
                          logActivity("workspace_layout_loaded", { index });
                        }}
                      >
                        <p className="text-sm font-semibold text-slate-900">
                          Layout {index + 1}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {typeof layout === "string" ? layout : "Layout data"}
                        </p>
                      </button>
                    ))}
              </div>
              {!loadingLayouts && layouts.length === 0 && (
                <div className="mt-4">
                  <EmptyState
                    title="No layouts yet"
                    message="Layouts will appear once configured."
                    testId="dashboard-empty-workspace-layouts"
                  />
                </div>
              )}
              {Number.isInteger(activeLayoutIndex) && layouts[activeLayoutIndex] && (
                <div className="mt-4">
                  {typeof layouts[activeLayoutIndex] === "string" ? (
                    <div className="card p-4 text-sm text-slate-600">
                      {layouts[activeLayoutIndex]}
                    </div>
                  ) : (
                    <KeyValueTable
                      data={layouts[activeLayoutIndex]}
                      title="Layout details"
                    />
                  )}
                </div>
              )}
              {layoutsError && (
                <div className="mt-4">
                  <ErrorState error={layoutsError} />
                </div>
              )}
            </div>
            <div className="mt-6">
              <p className="text-sm font-semibold text-slate-900">
                {workspaceData.dragDrop.title}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {workspaceData.dragDrop.subtitle}
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="card p-4">
                  <p className="text-xs text-slate-500">
                    {workspaceData.dragDrop.title}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {zones.map((zone) => (
                      <span
                        key={zone}
                        draggable
                        onDragStart={(event) =>
                          event.dataTransfer.setData("text/plain", zone)
                        }
                        className="cursor-grab rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600"
                      >
                        {zone}
                      </span>
                    ))}
                  </div>
                </div>
                <div
                  className="card p-4"
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    const zone = event.dataTransfer.getData("text/plain");
                    if (zone) {
                      handleZoneDrop(zone);
                    }
                  }}
                >
                  <p className="text-xs text-slate-500">
                    {workspaceData.dragDrop.subtitle}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {mappedZones.length === 0 ? (
                      <span className="text-xs text-slate-400">
                        {workspaceData.notes}
                      </span>
                    ) : (
                      mappedZones.map((zone) => (
                        <span
                          key={zone}
                          className="rounded-full bg-amber-100 px-3 py-1 text-xs text-amber-700"
                        >
                          {zone}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="mt-4 text-sm text-slate-600">{workspaceData.notes}</p>
        )}
      </div>

      {!dismissed && (
        <div className="card p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {workspaceData.guidance.title}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {workspaceData.guidance.description}
              </p>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => {
                localStorage.setItem("myv_guidance_workspace", "true");
                setDismissed(true);
                logActivity("guidance_dismissed", { page: "workspace" });
              }}
            >
              {workspaceData.guidance.ctaLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
